// import { useProgress } from '@react-three/drei';
import gsap from 'gsap';
import { useRef, useState } from 'react';
import AppPreloader from '../preloader/AppPreloader';
import LoadFontMethodTask from '../preloader/util/task-loader/LoadFontMethodTask';
import LoadProgressMethodTask from '../preloader/util/task-loader/LoadProgressMethodTask';
import useIsomorphicLayoutEffect from './useIsomorphicLayoutEffect';
import { useCountryContext } from '../context/countryContext';

const manualProgressTween = (
  data: { progress: number },
  duration: number,
  onUpdate: (progress: number) => void,
): Promise<void> =>
  new Promise<void>((resolve) => {
    gsap.to(data, {
      duration,
      onUpdate: () => {
        onUpdate(data.progress);
      },
      progress: 100,
      ease: 'none',
      onComplete: () => resolve(),
    });
  });

export const preloader = new AppPreloader();
const smoothProgress = { progress: 0 };
const smoothProgressDuration = process.env.NODE_ENV !== 'production' ? 0.01 : 0.01;

let FIRST_TIME = true;

export function useLoadGlobalAssets(): {
  isLoaded: boolean;
  progress: number;
} {
  const [isLoaded, setIsLoaded] = useState(typeof window === undefined);
  const [progress, setPreloaderProgress] = useState<number>(0);
  // const { progress: webglProgress } = useProgress();
  const webglProgressTask = useRef<LoadProgressMethodTask>(undefined);
  const fakeProgressTask = useRef<LoadProgressMethodTask>(undefined);
  const fontProgressTask = useRef<LoadFontMethodTask>(undefined);

  const { fetchCountry } = useCountryContext();

  useIsomorphicLayoutEffect(() => {
    // webglProgressTask.current = new LoadProgressMethodTask({ name: 'webgl', assets: [] });
    fontProgressTask.current = new LoadFontMethodTask({
      assets: ['StabilGrotesk'], // 'Noto Sans JP'
    });

    fakeProgressTask.current = new LoadProgressMethodTask({
      name: 'fake',
      assets: [],
    });

    return () => {
      if (isLoaded) {
        fontProgressTask.current?.dispose();
        fakeProgressTask.current?.dispose();
      }
      // webglProgressTask.current?.dispose();
    };
  }, []);

  // useEffect(() => {
  //   console.log(webglProgress, 'progress');
  //   webglProgressTask.current?.setProgress(webglProgress);
  // }, [webglProgress]);

  useIsomorphicLayoutEffect(() => {
    (async () => {
      if (typeof window !== 'undefined' && FIRST_TIME) {
        FIRST_TIME = false;
        if (fontProgressTask.current) {
          preloader.addTask(fontProgressTask.current);
        }

        if (fakeProgressTask.current) {
          preloader.addTask(fakeProgressTask.current);

          // We use this to have a minimum duration for having the site-preloader active + it smoothens out the progress of actual assets if that comes in batches.
          manualProgressTween(smoothProgress, smoothProgressDuration, (progress) => {
            fakeProgressTask.current?.setProgress(progress);
          });
        }

        if (webglProgressTask.current) {
          preloader.addTask(webglProgressTask.current);
        }

        await Promise.all([
          preloader.start({
            onUpdate: (progress) => {
              setPreloaderProgress(progress);
            },
          }),
          fetchCountry(),
        ]);
        // Once all promises are completed we know it's loaded.
        setIsLoaded(true);
      } else {
        setIsLoaded(true);
      }
    })();

    return () => {
      if (isLoaded) {
        preloader.dispose();
      }
    };
  }, []);

  return {
    isLoaded,
    progress,
  };
}
